import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ProfileSummary = ({ profileData, role, onEdit }) => {
  const getRoleTitle = () => {
    const titles = {
      contractor: 'Vakman',
      client: 'Opdrachtgever',
      affiliate: 'Partner',
      superadmin: 'Super Admin'
    };
    return titles?.[role] || 'Gebruiker';
  };

  const renderBasicInfo = () => (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
        <Icon name="User" size={20} />
        Basisinformatie
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-muted-foreground">Naam</p>
          <p className="text-foreground font-medium">{profileData?.full_name || 'Niet ingevuld'}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">E-mail</p>
          <p className="text-foreground font-medium">{profileData?.email || 'Niet ingevuld'}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Telefoonnummer</p>
          <p className="text-foreground font-medium">{profileData?.phone || 'Niet ingevuld'}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Locatie</p>
          <p className="text-foreground font-medium">{profileData?.location || 'Niet ingevuld'}</p>
        </div>
        {profileData?.company_name && (
          <div>
            <p className="text-sm text-muted-foreground">Bedrijfsnaam</p>
            <p className="text-foreground font-medium">{profileData?.company_name}</p>
          </div>
        )}
        <div>
          <p className="text-sm text-muted-foreground">Rol</p>
          <p className="text-foreground font-medium">{getRoleTitle()}</p>
        </div>
      </div>
    </div>
  );

  const renderContractorInfo = () => (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
        <Icon name="Wrench" size={20} />
        Vakman Gegevens
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-muted-foreground">Vaardigheden</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {profileData?.skills?.length > 0 ? (
              profileData?.skills?.map((skill, index) => (
                <span key={index} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-md">
                  {skill}
                </span>
              ))
            ) : (
              <p className="text-foreground">Niet ingevuld</p>
            )}
          </div>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Ervaringsniveau</p>
          <p className="text-foreground font-medium">{profileData?.experienceLevel || 'Niet ingevuld'}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Certificeringen</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {profileData?.certifications?.length > 0 ? (
              profileData?.certifications?.map((cert, index) => (
                <span key={index} className="px-2 py-1 bg-success/10 text-success text-xs rounded-md">
                  {cert}
                </span>
              ))
            ) : (
              <p className="text-foreground">Geen</p>
            )}
          </div>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Uurtarief</p>
          <p className="text-foreground font-medium">
            {profileData?.hourlyRate ? `€${profileData?.hourlyRate}/uur` : 'Niet ingevuld'}
          </p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Beschikbaarheid</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {profileData?.availability?.length > 0 ? (
              profileData?.availability?.map((day, index) => (
                <span key={index} className="px-2 py-1 bg-muted text-foreground text-xs rounded-md">
                  {day}
                </span>
              ))
            ) : (
              <p className="text-foreground">Niet ingevuld</p>
            )}
          </div>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Reisafstand</p>
          <p className="text-foreground font-medium">
            {profileData?.travelDistance ? `${profileData?.travelDistance} km` : 'Niet ingevuld'}
          </p>
        </div>
      </div>
    </div>
  );

  const renderClientInfo = () => (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
        <Icon name="Building2" size={20} />
        Opdrachtgever Gegevens
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-muted-foreground">Bedrijfstype</p>
          <p className="text-foreground font-medium">{profileData?.businessType || 'Niet ingevuld'}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">KVK Nummer</p>
          <p className="text-foreground font-medium">{profileData?.businessRegistration || 'Niet ingevuld'}</p>
        </div>
        <div className="md:col-span-2">
          <p className="text-sm text-muted-foreground">Factuuradres</p>
          <p className="text-foreground font-medium">{profileData?.billingAddress || 'Niet ingevuld'}</p>
        </div>
        <div className="md:col-span-2">
          <p className="text-sm text-muted-foreground">Dienstvoorkeuren</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {profileData?.servicePreferences?.length > 0 ? (
              profileData?.servicePreferences?.map((pref, index) => (
                <span key={index} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-md">
                  {pref}
                </span>
              ))
            ) : (
              <p className="text-foreground">Niet ingevuld</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderAffiliateInfo = () => (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
        <Icon name="Users" size={20} />
        Partner Gegevens
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-muted-foreground">Bedrijfsnaam</p>
          <p className="text-foreground font-medium">{profileData?.companyName || 'Niet ingevuld'}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Netwerktype</p>
          <p className="text-foreground font-medium">{profileData?.networkType || 'Niet ingevuld'}</p>
        </div>
        <div className="md:col-span-2">
          <p className="text-sm text-muted-foreground">Verwijzingsmogelijkheden</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {profileData?.referralCapabilities?.length > 0 ? (
              profileData?.referralCapabilities?.map((cap, index) => (
                <span key={index} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-md">
                  {cap}
                </span>
              ))
            ) : (
              <p className="text-foreground">Niet ingevuld</p>
            )}
          </div>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Commissie Structuur</p>
          <p className="text-foreground font-medium">{profileData?.preferredCommissionStructure || 'Niet ingevuld'}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Commissie Percentage</p>
          <p className="text-foreground font-medium">
            {profileData?.commissionRate ? `${profileData?.commissionRate}%` : 'Standaard (5%)'}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Profiel Overzicht</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={onEdit}
          iconName="Edit"
          iconPosition="left"
        >
          Wijzigen
        </Button>
      </div>

      <div className="space-y-6">
        {renderBasicInfo()}
        
        <div className="border-t border-border pt-6">
          {role === 'contractor' && renderContractorInfo()}
          {role === 'client' && renderClientInfo()}
          {role === 'affiliate' && renderAffiliateInfo()}
        </div>
      </div>
    </div>
  );
};

export default ProfileSummary;